// One row per CMS OAP measure: the FILED baseline (logic/baseline.ts — never measure_state's
// rolling column), the current value, the binding CMS goal and a deterministic status. The care
// plan's guideline target rides the tooltip only (D1).
// `bpCurrent` is WF37's quarterly pick through the shared rule; the gaps are in logic/bpSession.ts.

import {
  classifyDiagnoses, requiredMeasures, windowDaysFor, type DiagnosisClasses,
} from '../cmsRequiredMeasures';
import { deriveTrackCode } from '../track';
import {
  baselineMissingCopy, findBaselineFiling, filedBpBaseline, filedMeasureBaseline, type BaselineFilingRow, type MeasureStateRow,
} from '../baseline';
import { planTargetText } from '../carePlanTargets';
import {
  bpSessionCountLabel, bpSessionMean, bpSessionOf, localDayKey, BP_SESSION_MIN_READINGS,
} from '../bpSession';
import { KG_TO_LB } from '../patientHome';
import {
  goalSpecFor, resolveScalarGoal, resolveWeightGoal, type GoalRoute, type OapMeasure, type OapTrack, type TargetSpec,
} from './targets';

const DAY = 86_400_000;
export const BP_MIN_READINGS = BP_SESSION_MIN_READINGS;
export const WEIGHT_WINDOW_DAYS = 15;

export const MEASURE_ORDER: OapMeasure[] = ['bp', 'weight', 'hba1c', 'ldl_c', 'egfr', 'uacr'];

export type MeasureCategory = 'heart' | 'body' | 'respiratory' | 'neutral';

export const MEASURE_META: Record<OapMeasure, { label: string; unit: string; category: MeasureCategory; measureType: string }> = {
  bp: {
    label: 'Blood pressure', unit: 'mmHg', category: 'heart', measureType: 'bp_systolic',
  },
  weight: {
    label: 'Weight & BMI', unit: 'lb', category: 'body', measureType: 'weight',
  },
  hba1c: {
    label: 'HbA1c', unit: '%', category: 'respiratory', measureType: 'hba1c',
  },
  ldl_c: {
    label: 'LDL-C', unit: 'mg/dL', category: 'respiratory', measureType: 'ldl_c',
  },
  egfr: {
    label: 'eGFR', unit: 'mL/min/1.73m²', category: 'neutral', measureType: 'egfr',
  },
  uacr: {
    label: 'uACR', unit: 'mg/g', category: 'neutral', measureType: 'uacr',
  },
};

// WF37's slug vocabulary → the row key.
const SLUG_TO_MEASURE: Record<string, OapMeasure> = {
  systolic_bp: 'bp', weight: 'weight', bmi: 'weight', hba1c: 'hba1c', ldl: 'ldl_c', egfr: 'egfr', uacr: 'uacr',
};
// WF37's slugs for the measures whose CMS validity window applies to a chart value.
const LAB_SLUG: Partial<Record<OapMeasure, string>> = {
  hba1c: 'hba1c', ldl_c: 'ldl', egfr: 'egfr', uacr: 'uacr',
};
const ONE_DP = new Set<string>(['weight', 'hba1c', 'uacr', 'bmi']);

export type OapStatus = 'met' | 'improving' | 'not_met' | 'stale' | 'insufficient' | 'no_reading' | 'report_only' | 'advisory';

export interface SeriesPoint { epoch: number; value: number }
export interface BpReadingLike { epoch: number; systolic: number; diastolic: number }
// The status, flag and period fields come from MeasureStateRow: baseline.ts reads them for labs.
export interface MeasureStateLike extends MeasureStateRow {
  latest_value?: unknown;
  latest_at?: string | null;
}
export interface CarePlanLike { conditions?: unknown; condition_slugs?: unknown; targets?: unknown }

export interface OapGoal {
  display: string;
  route: GoalRoute;
  /** Both routes spelled out, for the tooltip. */
  detail: string;
  /** The care plan's own guideline target — tooltip only, never inline. */
  planTarget: string | null;
}

export interface OapRow {
  key: OapMeasure;
  label: string;
  unit: string;
  category: MeasureCategory;
  required: boolean;
  scored: boolean;
  /** What CMS received, or null when the filing does not record this measure. */
  baseline: { display: string } | null;
  /** Why `baseline` is null — not recorded, or a lab the track never files. Null when it is set. */
  baselineNote: string | null;
  current: { display: string; at: number | null; note: string | null } | null;
  series: SeriesPoint[] | null;
  goal: OapGoal | null;
  gap: string | null;
  status: OapStatus;
  statusLabel: string;
}

export interface OapEvaluation {
  track: OapTrack;
  filedAt: string | null;
  dx: DiagnosisClasses;
  rows: OapRow[];
}

export interface EvaluateInput {
  trackCode?: string | null;
  subtrackId?: string | null;
  carePlan?: CarePlanLike | null;
  measureStates?: readonly MeasureStateLike[] | null;
  cmsSubmissions?: readonly BaselineFilingRow[] | null;
  withingsUserId: string | number;
  /** Paired BP readings over the sparkline window (pairBpReadings), oldest first; null = not loaded. */
  bpReadings?: readonly BpReadingLike[] | null;
  /** Day-averaged weight in KG over the same window (buildWeightSeries), oldest first; null = not loaded. */
  weightReadings?: readonly SeriesPoint[] | null;
  /** The patient's IANA zone (patients.timezone): what "one day" means for BP. Null → New York. */
  timeZone?: string | null;
  now?: number;
}

/** WF37's rule: `condition_slugs` when present, else the display `conditions`. */
export function planConditions(plan: CarePlanLike | null | undefined): string[] {
  const slugs = plan?.condition_slugs;
  if (Array.isArray(slugs) && slugs.length > 0) return slugs.map(String);
  const conds = plan?.conditions;
  return Array.isArray(conds) ? conds.map(String) : [];
}

/** 'eckm' | 'ckm', or null — never the filing's eCKM default; an unknown track hides the board. */
export function oapTrackOf(trackCode?: string | null, subtrackId?: string | null): OapTrack | null {
  const t = deriveTrackCode(trackCode, subtrackId);
  return t === 'eckm' || t === 'ckm' ? t : null;
}

/** Measures CMS requires at the baseline OR the final filing, folded onto the six rows. */
export function requiredOapMeasures(track: OapTrack, conditions: readonly string[]): Set<OapMeasure> {
  const out = new Set<OapMeasure>();
  (['baseline', 'final'] as const).forEach((kind) => {
    requiredMeasures({ track, conditions: [...conditions], report_kind: kind }).required.forEach((slug: string) => {
      const m = SLUG_TO_MEASURE[slug];
      if (m) out.add(m);
    });
  });
  return out;
}

/** WF37's quarterly pick (`latest_day_3_in_15d`): the latest 3 of the most recent day with 3, rounded. */
export function bpCurrent(
  readings: readonly BpReadingLike[] | null | undefined,
  now: number,
  timeZone?: string | null,
): { n: number; sys: number | null; dia: number | null; at: number | null; day: string | null } {
  const s = bpSessionOf(readings, { nowMs: now, reportKind: 'quarterly', timeZone });
  const last = s.readings.length ? s.readings[s.readings.length - 1]! : null;
  const at = last ? Math.floor(last.ms / 1000) : null;
  const mean = s.ok ? bpSessionMean(s.readings) : null;
  return {
    n: s.found, sys: mean?.sys ?? null, dia: mean?.dia ?? null, at, day: s.day,
  };
}

/** Rounded to what the row prints — the verdict is computed on the printed number. */
export function roundForDisplay(value: number, measure: OapMeasure | 'bmi'): number {
  const f = ONE_DP.has(measure) ? 10 : 1;
  return Math.round(value * f) / f;
}

const fmt = (v: number, measure: OapMeasure | 'bmi'): string => {
  const r = roundForDisplay(v, measure);
  return ONE_DP.has(measure) ? r.toFixed(1) : String(r);
};

// One mean per patient day (the zone bpCurrent uses), so the sparkline draws days, not a same-morning cluster.
function daySeries<T extends { epoch: number }>(rows: readonly T[], pick: (r: T) => number, timeZone?: string | null): SeriesPoint[] {
  const dayOf = localDayKey(timeZone);
  const byDay = new Map<string, { epoch: number; sum: number; n: number }>();
  rows.forEach((r) => {
    const key = dayOf(r.epoch);
    const slot = byDay.get(key) ?? { epoch: r.epoch, sum: 0, n: 0 };
    slot.sum += pick(r);
    slot.n += 1;
    slot.epoch = r.epoch;
    byDay.set(key, slot);
  });
  return Array.from(byDay.values()).map((s) => ({ epoch: s.epoch, value: s.sum / s.n })).sort((a, b) => a.epoch - b.epoch);
}

const STATUS_LABEL: Record<OapStatus, string> = {
  met: 'Met — protect',
  improving: 'Not met · improving',
  not_met: 'Not met',
  stale: 'Stale',
  insufficient: 'Insufficient readings',
  no_reading: 'No reading',
  report_only: 'Reported (no target)',
  advisory: 'Advisory',
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function sign(delta: number): string {
  if (delta > 0) return '+';
  if (delta < 0) return '−';
  return '±';
}
const signed = (delta: number, unit: string, measure: OapMeasure): string => `${sign(delta)}${fmt(Math.abs(delta), measure)}${unit === '%' ? '' : ' '}${unit}`;

function scalarStatus(value: number, baseline: number | null, target: number, route: GoalRoute): OapStatus {
  const met = route === 'control' ? value < target : value <= target;
  if (met) return 'met';
  return baseline != null && value < baseline ? 'improving' : 'not_met';
}

interface Ctx {
  track: OapTrack;
  dx: DiagnosisClasses;
  required: Set<OapMeasure>;
  planTargets: unknown;
  byType: Map<string, MeasureStateLike>;
  subs: BaselineFilingRow[] | null;
  uid: string | number;
  now: number;
  input: EvaluateInput;
}

type Draft = Omit<OapRow, 'statusLabel' | 'label' | 'unit' | 'category' | 'required' | 'key' | 'baselineNote'>;

function finish(key: OapMeasure, ctx: Ctx, d: Draft, insufficientN?: number): OapRow {
  const meta = MEASURE_META[key];
  let statusLabel = STATUS_LABEL[d.status];
  if (d.status === 'insufficient') {
    statusLabel = key === 'bp'
      ? bpSessionCountLabel(insufficientN ?? 0)
      : `${STATUS_LABEL.insufficient} (${insufficientN ?? 0}/${BP_MIN_READINGS})`;
  }
  const baselineNote = d.baseline ? null : baselineMissingCopy(ctx.subs, ctx.uid, { measureType: meta.measureType, track: ctx.track });
  return {
    key, label: meta.label, unit: meta.unit, category: meta.category, required: ctx.required.has(key), ...d, baselineNote, statusLabel,
  };
}

function buildBp(ctx: Ctx, spec: TargetSpec | null): OapRow {
  const filed = filedBpBaseline(ctx.subs, ctx.uid);
  const baseline = filed ? { display: `${filed.sys}/${filed.dia ?? '—'}` } : null;
  const readings = ctx.input.bpReadings ?? [];
  const series = ctx.input.bpReadings ? daySeries(readings, (r) => r.systolic, ctx.input.timeZone) : null;
  const cur = bpCurrent(readings, ctx.now, ctx.input.timeZone);
  const scalar = spec?.kind === 'scalar' ? resolveScalarGoal(spec, ctx.dx, filed?.sys ?? null) : null;
  const goal: OapGoal | null = scalar ? {
    display: scalar.route === 'control' ? `< ${scalar.controlLt}` : `≤ ${scalar.target}`,
    route: scalar.route,
    detail: `CMS: systolic < ${scalar.controlLt}, or −15 from the filed baseline${scalar.improvementTarget != null ? ` (≤ ${scalar.improvementTarget})` : ''}. Met if either holds.`,
    planTarget: planTargetText(ctx.planTargets, 'bp_systolic'),
  } : null;
  if (cur.sys == null || cur.dia == null) {
    return finish('bp', ctx, {
      scored: !!goal, baseline, current: null, series, goal, gap: null, status: readings.length === 0 ? 'no_reading' : 'insufficient',
    }, cur.n);
  }
  const current = { display: `${cur.sys}/${cur.dia}`, at: cur.at, note: null };
  if (!scalar || !goal) {
    return finish('bp', ctx, {
      scored: false, baseline, current, series, goal: null, gap: null, status: 'report_only',
    });
  }
  return finish('bp', ctx, {
    scored: true,
    baseline,
    current,
    series,
    goal,
    gap: signed(cur.sys - scalar.target, 'mmHg', 'bp'),
    status: scalarStatus(cur.sys, filed?.sys ?? null, scalar.target, scalar.route),
  });
}

function buildWeight(ctx: Ctx, spec: TargetSpec | null): OapRow {
  const baseKg = filedMeasureBaseline('weight', ctx.subs, ctx.uid);
  const baseBmi = filedMeasureBaseline('bmi', ctx.subs, ctx.uid);
  const baseline = baseKg != null
    ? { display: `${fmt(baseKg * KG_TO_LB, 'weight')} lb${baseBmi != null ? ` · BMI ${fmt(baseBmi, 'bmi')}` : ''}` }
    : null;
  const points = ctx.input.weightReadings ?? [];
  const series = ctx.input.weightReadings ? points.map((p) => ({ epoch: p.epoch, value: p.value * KG_TO_LB })) : null;
  const latest = points.length ? points[points.length - 1]! : null;
  const fresh = latest && latest.epoch * 1000 >= ctx.now - WEIGHT_WINDOW_DAYS * DAY ? latest : null;
  const weightSpec = spec?.kind === 'weight' ? spec : null;
  const wg = weightSpec && baseKg != null ? resolveWeightGoal(weightSpec, baseKg, baseBmi) : null;
  const targetLb = wg ? roundForDisplay(wg.targetKg * KG_TO_LB, 'weight') : null;

  let goalDisplay: string | null = null;
  if (weightSpec) {
    if (wg && targetLb != null) {
      goalDisplay = wg.route === 'control'
        ? `BMI < ${weightSpec.controlBmiLt} · ≤ ${targetLb.toFixed(1)} lb`
        : `≤ ${targetLb.toFixed(1)} lb (−${Math.abs(weightSpec.improvementPct)}%)`;
    } else {
      goalDisplay = `BMI < ${weightSpec.controlBmiLt}`;
    }
  }
  const goal: OapGoal | null = weightSpec && goalDisplay ? {
    display: goalDisplay,
    route: wg?.route ?? 'control',
    detail: `CMS: BMI < ${weightSpec.controlBmiLt} with no more than ${weightSpec.controlMaxGainPct}% weight gain, or ${Math.abs(weightSpec.improvementPct)}% weight loss from the filed baseline. Met if either holds.`,
    planTarget: planTargetText(ctx.planTargets, 'weight') ?? planTargetText(ctx.planTargets, 'bmi'),
  } : null;

  if (!fresh) {
    return finish('weight', ctx, {
      scored: !!goal, baseline, current: null, series, goal, gap: null, status: points.length ? 'stale' : 'no_reading',
    });
  }
  const bmiRow = ctx.byType.get('bmi');
  const bmiAt = bmiRow?.latest_at ? Date.parse(String(bmiRow.latest_at)) : NaN;
  const bmiFresh = Number.isFinite(bmiAt) && bmiAt >= ctx.now - WEIGHT_WINDOW_DAYS * DAY;
  const msBmi = bmiFresh ? num(bmiRow?.latest_value) : null;
  const derivedBmi = baseKg != null && baseBmi != null ? fresh.value * (baseBmi / baseKg) : null;
  const curBmi = msBmi ?? derivedBmi;
  const curLb = roundForDisplay(fresh.value * KG_TO_LB, 'weight');
  const current = { display: `${curLb.toFixed(1)} lb${curBmi != null ? ` · BMI ${fmt(curBmi, 'bmi')}` : ''}`, at: fresh.epoch, note: null };
  if (!weightSpec || !goal) {
    return finish('weight', ctx, {
      scored: false, baseline, current, series, goal: null, gap: null, status: 'report_only',
    });
  }
  const bmiOk = curBmi != null && roundForDisplay(curBmi, 'bmi') < weightSpec.controlBmiLt;
  if (!wg || baseKg == null || targetLb == null) {
    // No filed weight: only the BMI half of control is knowable, and only with a BMI to judge.
    let status: OapStatus = 'no_reading';
    if (curBmi != null) status = bmiOk ? 'met' : 'not_met';
    return finish('weight', ctx, {
      scored: true, baseline, current, series, goal, gap: null, status,
    });
  }
  const gainCapLb = roundForDisplay(baseKg * (1 + weightSpec.controlMaxGainPct / 100) * KG_TO_LB, 'weight');
  const lossLb = roundForDisplay(wg.improvementKg * KG_TO_LB, 'weight');
  const baseLb = roundForDisplay(baseKg * KG_TO_LB, 'weight');
  const met = (bmiOk && curLb <= gainCapLb) || curLb <= lossLb;
  let status: OapStatus = 'not_met';
  if (met) status = 'met';
  else if (curLb < baseLb) status = 'improving';
  return finish('weight', ctx, {
    scored: true, baseline, current, series, goal, gap: signed(curLb - targetLb, 'lb', 'weight'), status,
  });
}

// Labs and the kidney measures: one value per measure_state row, no series (phase 4).
function buildLab(key: OapMeasure, ctx: Ctx, spec: TargetSpec | null): OapRow | null {
  const meta = MEASURE_META[key];
  const isRequired = ctx.required.has(key);
  const ms = ctx.byType.get(meta.measureType);
  const value = num(ms?.latest_value);
  const filedValue = filedMeasureBaseline(meta.measureType, ctx.subs, ctx.uid, {
    measureStates: ctx.input.measureStates, track: ctx.track,
  });
  if (!isRequired && value == null && filedValue == null) return null;
  const baseline = filedValue != null ? { display: fmt(filedValue, key) } : null;
  const parsed = ms?.latest_at ? Date.parse(String(ms.latest_at)) : NaN;
  const atMs = Number.isFinite(parsed) ? parsed : null;
  const slug = LAB_SLUG[key];
  const limit = slug ? windowDaysFor(slug, ctx.dx) : null;
  const stale = atMs != null && limit != null && ctx.now - atMs > limit * DAY;
  const current = value != null
    ? { display: fmt(value, key), at: atMs == null ? null : Math.floor(atMs / 1000), note: atMs == null ? 'collection date not recorded' : null }
    : null;

  if (!spec || spec.kind !== 'scalar') {
    // A report-only value past its CMS window is not a valid report either.
    let status: OapStatus = 'advisory';
    if (value == null) status = 'no_reading';
    else if (stale) status = 'stale';
    else if (isRequired) status = 'report_only';
    return finish(key, ctx, {
      scored: false, baseline, current, series: null, goal: null, gap: null, status,
    });
  }
  const scalar = resolveScalarGoal(spec, ctx.dx, filedValue);
  const unitSuffix = key === 'hba1c' ? '%' : ` ${meta.unit}`;
  const improvement = spec.improvementDelta == null
    ? '. Control is the only route on this track.'
    : `, or ${fmt(spec.improvementDelta, key)}${unitSuffix} from the filed baseline${scalar.improvementTarget != null ? ` (≤ ${fmt(scalar.improvementTarget, key)})` : ''}. Met if either holds.`;
  const goal: OapGoal = {
    display: scalar.route === 'control' ? `< ${fmt(scalar.controlLt, key)}` : `≤ ${fmt(scalar.target, key)}`,
    route: scalar.route,
    detail: `CMS: < ${fmt(scalar.controlLt, key)}${unitSuffix}${improvement}`,
    planTarget: planTargetText(ctx.planTargets, meta.measureType),
  };
  if (value == null) {
    return finish(key, ctx, {
      scored: true, baseline, current: null, series: null, goal, gap: null, status: 'no_reading',
    });
  }
  const rounded = roundForDisplay(value, key);
  const target = roundForDisplay(scalar.target, key);
  const verdict = scalarStatus(rounded, filedValue, target, scalar.route);
  return finish(key, ctx, {
    scored: true,
    baseline,
    current,
    series: null,
    goal,
    gap: signed(rounded - target, meta.unit, key),
    // A value past its CMS window cannot be filed, so it cannot be "met".
    status: stale ? 'stale' : verdict,
  });
}

// Null = must not render: unknown track (fail closed) or no accepted baseline filing (D2).
export function evaluateOapRows(input: EvaluateInput): OapEvaluation | null {
  const track = oapTrackOf(input.trackCode, input.subtrackId);
  if (!track) return null;
  const subs = input.cmsSubmissions ? [...input.cmsSubmissions] : null;
  const filing = findBaselineFiling(subs, input.withingsUserId);
  if (!filing) return null;

  const conditions = planConditions(input.carePlan);
  const dx = classifyDiagnoses(conditions);
  const byType = new Map<string, MeasureStateLike>();
  (input.measureStates || []).forEach((ms) => { if (ms?.measure_type) byType.set(String(ms.measure_type), ms); });
  const ctx: Ctx = {
    track,
    dx,
    required: requiredOapMeasures(track, conditions),
    planTargets: input.carePlan?.targets,
    byType,
    subs,
    uid: input.withingsUserId,
    now: input.now ?? Date.now(),
    input,
  };

  const rows = MEASURE_ORDER
    .map((key) => {
      const spec = goalSpecFor(track, key, dx);
      if (key === 'bp') return buildBp(ctx, spec);
      if (key === 'weight') return buildWeight(ctx, spec);
      return buildLab(key, ctx, spec);
    })
    .filter((r): r is OapRow => r != null);

  return {
    track, filedAt: filing.at, dx, rows,
  };
}
